// ColorButton.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ColorButton.css';

// Components
import Popup from 'components/popup/Popup';

const TRANSPARENT_OPTION_KEY = '__transparent__';

/**
 * ColorButton
 *
 * Renders a color picker button with retro palette options and a transparent state.
 *
 * @param {string} color - The currently selected hex color, or an empty string for transparent.
 * @param {function} onChange - Called with the next selected color value.
 * @param {boolean} disabled - Whether the color picker should be disabled.
 * @param {number} popupZIndex - The z-index applied to the palette popup.
 *
 * @returns {ReactNode} The rendered ColorButton.
 */
const ColorButton = React.memo(({
    color = '',
    onChange,
    disabled = false,
    popupZIndex = 2000,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();
    
    const colorOptions = theme.colorOptions || [];
    const isTransparentSelected = !color;

    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleSelect = (nextColor) => {
        if (disabled) return;
        onChange?.(nextColor);
        setOpen(false);
    };

    return (
        <>
            {/* TRIGGER */}
            <div
                className={`color-button-container ${disabled ? 'disabled' : ''}`}
                style={{
                    borderColor: theme.backgroundMuted,
                }}
                ref={anchorRef}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-label="Select color"
                onClick={() => {
                    if (!disabled) setOpen(true);
                }}
                onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                <div
                    className={`color-button-swatch ${color ? '' : 'empty'}`}
                    style={{
                        backgroundColor: color || 'transparent',
                        borderColor: theme.backgroundMuted,
                    }}
                />
            </div>

            <Popup anchorRef={anchorRef} isOpen={open} setOpen={setOpen} zIndex={popupZIndex}>
                {/* PALETTE */}
                <div
                    className="color-button-popup"
                    style={{
                        backgroundColor: theme.backgroundMuted,
                        color: theme.foreground,
                    }}
                >
                    <button
                        className={`color-button-option color-button-option-transparent ${isTransparentSelected ? 'selected' : ''}`}
                        type="button"
                        aria-label="Select transparent"
                        onClick={() => handleSelect('')}
                    />

                    {colorOptions.map((swatch) => {
                        const isSelected = swatch === color;
                        return (
                            <button
                                className={`color-button-option ${isSelected ? 'selected' : ''}`}
                                style={{ backgroundColor: swatch }}
                                key={`${TRANSPARENT_OPTION_KEY}-${swatch}`}
                                type="button"
                                onClick={() => handleSelect(swatch)}
                                aria-label={`Select ${swatch}`}
                            />
                        );
                    })}
                </div>
            </Popup>
        </>
    );

});

// Dev Tools
ColorButton.displayName = 'ColorButton';

export default ColorButton;

